import React, { useEffect, useState } from "react";
import MainLogo from "../assets/images/AstraLogo.svg";
import { Link, useLocation } from "react-router-dom";
import ContactUsModal from "../components/ContactUs";
import { useSelector,useDispatch } from "react-redux";
import { getClientsChatList } from "../shared/_services/common.services";
import { setUnseenCount } from "../redux/chatReducer";
import socket from "../shared/_helpers/socket";
import { useAuth } from "../context/AuthContext.jsx";

const ADMIN_ROLES = ["super-admin", "admin"];

function getCurrentUserFromStorage() {
  try {
    return JSON.parse(localStorage.getItem("currentUser"))?.user || {};
  } catch (error) {
    return {};
  }
}

function isPrivilegedAdmin(role) {
  return ADMIN_ROLES.includes(role);
}

function isClientsPathActive(pathname, currentUserDataId) {
  const matchedClientPaths = currentUserDataId
    ? [`/dashboard/${currentUserDataId}`, `/meetings/${currentUserDataId}`, `/documents/${currentUserDataId}`]
    : [];
  return pathname === "/users" || matchedClientPaths.includes(pathname);
}

function getLogoPath(role) {
  if (role === "client") return "/dashboard";
  if (isPrivilegedAdmin(role)) return "/admin-dashboard";
  if (role === "advisor") return "/advisor-meetings";
  return null;
}

function getClientDashboardPath(pathname) {
  return pathname === "/dashboard-zero" ? "/dashboard-zero" : "/dashboard";
}

function isClientDashboardActive(pathname) {
  return pathname === "/dashboard" || pathname === "/dashboard-zero";
}

function isMeetingsActive(pathname) {
  return ["/meetings", "/advisor-meetings", "/advisor-manage-availability"].includes(pathname);
}

function buildSidebarItems({ role, pathname, currentUserDataId, unseenCount }) {
  const items = [];

  if (role === "client") {
    items.push({
      key: "client-dashboard",
      to: getClientDashboardPath(pathname),
      icon: "iconoir-home-simple-door",
      label: "Dashboard",
      active: isClientDashboardActive(pathname),
      extraClass: "dashbord-item",
    });
  }

  if (isPrivilegedAdmin(role)) {
    items.push({
      key: "admin-dashboard",
      to: "/admin-dashboard",
      icon: "iconoir-home-simple-door",
      label: "Dashboard",
      active: pathname === "/admin-dashboard",
      extraClass: "dashbord-item",
    });
  }

  if (!isPrivilegedAdmin(role)) {
    items.push({
      key: "meetings",
      to: role === "advisor" ? "/advisor-meetings" : "/meetings",
      icon: "iconoir-facetime",
      label: "Meetings",
      active: isMeetingsActive(pathname),
    });
  }

  if (["super-admin", "advisor", "admin"].includes(role)) {
    items.push({
      key: "clients",
      to: "/users",
      icon: "iconoir-community",
      label: "Clients",
      active: isClientsPathActive(pathname, currentUserDataId),
    });
  }

  if (role === "advisor") {
    items.push({
      key: "messages",
      to: "/message",
      icon: "iconoir-message-text",
      label: "Messages",
      active: pathname === "/message",
      badge: unseenCount > 0 ? unseenCount : null,
    });
  }

  if (isPrivilegedAdmin(role)) {
    items.push({
      key: "advisors",
      to: "/advisors",
      icon: "iconoir-user-badge-check",
      label: "Advisors",
      active: pathname === "/advisors",
    });
    items.push({
      key: "investment-tools",
      to: "/investment-tool",
      icon: "iconoir-privacy-policy",
      label: "Investment Tools",
      active: pathname === "/investment-tool",
    });
  }

  if (role === "super-admin") {
    items.push({
      key: "packages",
      to: "/package-management",
      icon: "iconoir-dollar-circle",
      label: "Packages",
      active: pathname === "/package-management",
    });
    items.push({
      key: "admins",
      to: "/admins",
      icon: "iconoir-user-crown",
      label: "Admin",
      active: pathname === "/admins",
    });
  }

  if (role === "client") {
    items.push({
      key: "documents",
      to: "/documents",
      icon: "iconoir-multiple-pages",
      label: "Documents",
      active: pathname === "/documents",
    });
    items.push({
      key: "questionnaire",
      to: "/questionnaire",
      icon: "iconoir-page-search",
      label: "Questionnaire",
      active: pathname === "/questionnaire",
    });
  }

  items.push({
    key: "profile",
    to: "/profile",
    icon: "iconoir-user-circle",
    label: "Profile",
    active: pathname === "/profile",
  });

  return items;
}

const DashboardSidebar = () => {
  const path = useLocation();
  const dispatch = useDispatch();

  const [contactUsPopupShow, setContactUsPopupShow] = useState(false);
  // SEC-066: which admin menu items render is gated by the server-verified
  // role, not the localStorage copy — this is rendered inside
  // ProtectedRoutes, so verification has already settled by the time this
  // mounts.
  const { role } = useAuth();
  const currentUserDataId = getCurrentUserFromStorage()?.data?._id;
  const unseenCount = useSelector((state) => state.unseenMessages.unseenCount);
  const logoPath = getLogoPath(role);
  const sidebarItems = buildSidebarItems({
    role,
    pathname: path.pathname,
    currentUserDataId,
    unseenCount,
  });
  const handleContactUsClick = () => {
    setContactUsPopupShow(true);
  };

  useEffect(() => {
          const fetchChatData = async () => {
              const response = await getClientsChatList();
              const storeChatData = response?.data?.data || [];
              const filteredChatData = storeChatData.filter(chat => chat.lastMessage?.id?.content);
              const newChatData = filteredChatData.sort((a, b) => {
                  const dateA = new Date(a.lastMessage?.id?.updatedAt || a.updatedAt);
                  const dateB = new Date(b.lastMessage?.id?.updatedAt || b.updatedAt);
                  return dateB - dateA;
              });
              // setChatData(newChatData);
              // Calculate unseen count directly from the response data
              // const unseenCount = newChatData.filter(chat => !chat.lastMessage.seen).length;
  
              const currentUserId = JSON.parse(localStorage?.getItem('currentUser'))?.user?._id;
  
              const unseenCount = newChatData.filter(chat =>
                  !chat.lastMessage?.seen && chat.lastMessage?.id?.sender !== currentUserId
              ).length;
  
              // Dispatch the count to Redux
              dispatch(setUnseenCount(unseenCount));
          };
  
          fetchChatData();
          
          socket.on("chat list updated", fetchChatData); // Refresh instantly when triggered

          return () => {
            socket.off("chat list updated", fetchChatData);
          };
      }, [dispatch]);

  return (
    <div className="dashboard-sidebar" style={{display: 'flex', flexDirection: 'column'}}>
      <div style={{flex: '1'}}>
        <div className="text-center">
          {logoPath && (
            <Link className="logo" to={logoPath}>
              <img src={MainLogo} alt="" />
            </Link>
          )}
        </div>
        <div className="dashboard-menu">
          {sidebarItems.map((item) => (
            <Link
              key={item.key}
              to={item.to}
              className={`dashboard-menu-item ${item.extraClass || ""} ${item.active ? "active" : ""}`.trim()}
            >
              <i className={item.icon}></i>
              {item.label}
              {item.badge && <span className="message-count">{item.badge}</span>}
            </Link>
          ))}
        </div>
      </div>
      {role !== 'super-admin' && (
        < div className="sidebar-conact-box">
          <p className="text-center">
            Having problems with your dashboard? Connect with Customer Support.
          </p>
          <button
            type="button"
            onClick={handleContactUsClick}
            className="as-btn-primary w-100"
          >
            Contact Us
          </button>
          {contactUsPopupShow && <ContactUsModal onClose={() => setContactUsPopupShow(false)} />}
        </div>)}
    </div >
  );
};
export default DashboardSidebar;
